import './base-page-layout.scss';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PageTitle from './page-title/page-title';
import ReturnGuardWarning from '../../return-guard-warning-component/return-guard-warning';
import { useResizeDetector } from 'react-resize-detector';
import { isSet } from '../../../models/model-types-methods';
import { useEffectEvent } from '@floating-ui/react/utils';
import appLayoutStore from '../../../stores/app-layout-store';


class StickyElementsMap {
    private _map: Map<HTMLDivElement, number>;

    constructor() {
        this._map = new Map<HTMLDivElement, number>();
    }

    set(div: HTMLDivElement, height: number): void {
        this._map.set(div, height);
    }

    delete(div: HTMLDivElement): void {
        this._map.delete(div);
    }

    height(): number {
        let sum = 0;
        this._map.forEach((value) => sum += value);
        return sum;
    }
}


interface PageLayoutProps {
    children: React.ReactNode;
    title: string;
    hasBack?: boolean;
    subTitles?: (string | undefined)[];
    subTitleActions?: React.ReactNode;
    instruction?: React.ReactNode;
    titleActions?: React.ReactNode;
    backButtonCallback?: () => void;
    __testMode?: boolean; /** this prop is used only for tests to avoid rendering ReturnGuardWarning which requires dashboard translations context */
}

export interface IPageContext {
    get title(): string;
    get width(): number | undefined;
    get subTitles(): (string|undefined)[] | undefined;
    get subTitleActions(): React.ReactNode | undefined;
    get instruction(): React.ReactNode | undefined;
    backButtonCallback: (() => void) | undefined;
    containerRef: React.RefObject<HTMLDivElement>;
    setTitle: (val:string) => void,
    setSubTitles: (val: (string|undefined)[] | undefined) => void,
    setSubTitleActions: (val?: React.ReactNode) => void,
    setInstruction: (val?:React.ReactNode) => void,
    getTitleHeight: () => number,
    getPageTopOffset: () => number,
    setStickyHeadersHeight: (elem: HTMLDivElement|null, height: number) => void,
    unsetStickyHeadersHeight: (elem: HTMLDivElement|null) => void,
    setHasBack: (value: boolean) => void,
    setBottomPadding: (height: number|null) => void,
    setBackButtonCallback: (cb: () => void) => void,
}

export const PageContext = React.createContext<IPageContext>({} as IPageContext);

const BasePageLayout = ({ title, subTitles, subTitleActions, children, instruction, titleActions, hasBack, backButtonCallback, __testMode }: (PageLayoutProps)) => {
    const pageContainerRef = useRef<HTMLDivElement>(null);
    const [_title, setTitle] = useState(title);
    const [_subTitles, setSubTitles] = useState(subTitles);
    const [_subTitleActions, setSubTitleActions] = useState(subTitleActions);
    const [_instruction, setInstruction] = useState(instruction);
    const [_titleHeight, setTitleHeight] = useState(0);
    const _stickyElementsMap = useMemo(() => new StickyElementsMap(), []);
    const [_hasBack, setHasBack] = useState(hasBack);
    const [_bottomPadding, _setBottomPadding] = useState<number|null>(null);
    const [_backButtonCallback, _setBackButtonCallback] = useState<(() => void) | undefined>(() => backButtonCallback);
    const [_pageWidth, _setPageWidth] = useState<number | undefined>();

    /** BUG: some of those methods update local state from observers (see onTitleHeighChange for example)
     * This creates bug when base page layout was unmounted and observer fired which tries to update state of unmounted component
     * TODO: move all state management into local mobx store */

    useEffect(() => {
        setTitle(title);
    }, [title]);

    useEffect(() => {
        setSubTitles(subTitles);
    }, [subTitles]);

    useEffect(() => {
        setSubTitleActions(subTitleActions);
    }, [subTitleActions]);

    useEffect(() => {
        setInstruction(instruction);
    }, [instruction]);

    useEffect(() => {
        _setBackButtonCallback(() => backButtonCallback);
    }, [backButtonCallback]);

    const getTitleHeight = () => _titleHeight;

    /*
     * Explanation:
     *  onTitleHeighChange() which calls setState behind the scenes is called from within <PageTitle /> from direct method call
     *   before whole tree is rendered
     *  To control when to call setState(), following pattern is used here:
     *  - maintain ref
     *  - mutate it's value when <PageTitle /> is calling onTitleHeighChange()
     *  - via useEffect - call useState when tree is rendered and value of ref is changed
     */
    const _titleHeightRef = useRef(0);
    const onTitleHeighChange = (height: number) => {
        _titleHeightRef.current = height;
    }
    useEffect(() => {
        setTitleHeight(_titleHeightRef.current);
    }, [_titleHeightRef.current]);

    const getPageTopOffset = () => _titleHeight + _stickyElementsMap.height() + appLayoutStore.headerHeight;

    const setStickyHeadersHeight = useCallback((elem: HTMLDivElement|null, height: number) => {
        if (elem) {
            _stickyElementsMap.set(elem, height);
        }
    }, [_stickyElementsMap]);
    const unsetStickyHeadersHeight = useCallback((elem: HTMLDivElement|null) => {
        if (elem) {
            _stickyElementsMap.delete(elem);
        }
    }, [_stickyElementsMap]);

    const setBottomPadding = useCallback((height: number|null) => {
        _setBottomPadding(height);
    }, [_setBottomPadding]);

    const setBackButtonCallback = useCallback((cb: () => void) => {
        _setBackButtonCallback(() => cb);
    }, [_setBackButtonCallback]);

    /** TODO: fix, context object should be constructed once and not each render cycle */
    const contextValue: IPageContext = {
        title: _title,
        width: _pageWidth,
        subTitles: _subTitles,
        subTitleActions: _subTitleActions,
        instruction: _instruction,
        backButtonCallback: _backButtonCallback,
        containerRef: pageContainerRef,
        setTitle,
        setSubTitles,
        setSubTitleActions,
        setInstruction,
        getTitleHeight,
        getPageTopOffset,
        setStickyHeadersHeight,
        unsetStickyHeadersHeight,
        setHasBack,
        setBottomPadding,
        setBackButtonCallback
    };

    useResizeDetector<HTMLDivElement>({
        targetRef: pageContainerRef,
        observerOptions: { box: 'border-box' },
        onResize: ({ width }) => {
            if (pageContainerRef.current && isSet(width)) {
                _setPageWidth(width);
            }
        },
    });

    return (
        <PageContext.Provider value={contextValue}>
        <div className="page" ref={pageContainerRef}>
            {!__testMode && <ReturnGuardWarning />}
            <PageTitle onHeightChange={onTitleHeighChange} hasBack={_hasBack} title={_title} subTitles={_subTitles} subTitleActions={_subTitleActions} instruction={_instruction} titleActions={titleActions} backButtonCallback={_backButtonCallback} />
            {children}
            {!!_bottomPadding && <div style={{height: _bottomPadding}}></div>}
        </div>
        </PageContext.Provider>
    );
}

export default BasePageLayout;
